import { request } from './api-client';
import {
  SegmentDetailSchema,
  SegmentPreviewSchema,
  SegmentSummarySchema,
  pagedSchema,
  type SegmentDetail,
  type SegmentGroup,
  type SegmentPreview,
} from './schemas';
import { z } from 'zod';

const SegmentPageSchema = pagedSchema(SegmentSummarySchema);

export async function listSegments(params: {
  take?: number;
  skip?: number;
}): Promise<z.infer<typeof SegmentPageSchema>> {
  const search = new URLSearchParams();

  if (params.take !== undefined) search.set('take', String(params.take));
  if (params.skip !== undefined) search.set('skip', String(params.skip));

  const suffix = search.size > 0 ? `?${search.toString()}` : '';

  return request(`/api/v1/segments${suffix}`, SegmentPageSchema);
}

export async function getSegment(segmentId: string): Promise<SegmentDetail> {
  return request(`/api/v1/segments/${segmentId}`, SegmentDetailSchema);
}

export async function saveSegment(input: {
  segmentId?: string;
  brandId: string;
  name: string;
  description: string | null;
  rule: SegmentGroup;
}): Promise<SegmentDetail> {
  const body = {
    brandId: input.brandId,
    name: input.name,
    description: input.description,
    rule: input.rule,
  };

  return input.segmentId === undefined
    ? request('/api/v1/segments', SegmentDetailSchema, { method: 'POST', body })
    : request(`/api/v1/segments/${input.segmentId}`, SegmentDetailSchema, { method: 'PUT', body });
}

/**
 * Count and sample for an UNSAVED rule, so the builder can show the size as the author edits.
 *
 * On the authoring rate limit server-side, not the write one, because it runs on every debounced
 * change and writes nothing.
 */
export async function previewSegment(input: {
  brandId: string;
  rule: SegmentGroup;
  sampleSize: number;
}): Promise<SegmentPreview> {
  return request('/api/v1/segments/preview', SegmentPreviewSchema, {
    method: 'POST',
    body: input,
  });
}

export async function recountSegment(segmentId: string): Promise<number> {
  return request(`/api/v1/segments/${segmentId}/recount`, z.number().int(), { method: 'POST' });
}
